import {
  getPrimaryInstrumentFromMedia,
  SongCompositionDTO,
  StemType,
  TICKS_PER_LOOP
} from '@arpeggi/shared'
import { toJS } from 'mobx'
import ArpRegistry from '../../../features/arp-registry/ArpRegistry'
import { IAudioStemStore } from '../../stores/song/stem/AudioStemStore'
import { IDrumStemStore } from '../../stores/song/stem/DrumStemStore'
import { IInstrumentStemStore } from '../../stores/song/stem/InstrumentStemStore'
import { IStemStore } from '../../stores/song/StemStore'
import { IStudioStore } from '../../stores/StudioStore'

export const StudioDraggableTypes = {
  SAMPLE: 'studio-sample',
  STEM: 'studio-stem',
  TRACK: 'studio-track',
  LOOP: 'studio-loop',
  CLIP: 'studio-clip'
}

export const ticksToSeconds = (ticks: number, tempo: number): number => {
  return (60 / tempo) * (ticks / TICKS_PER_LOOP) * 16
}

export const getDrumStemSample = (stem: IStemStore, drumIndex: number) => {
  return ArpRegistry.readyForStudioUse
    ? ArpRegistry.retrieveFromCache((stem as IDrumStemStore).getDrumSample(drumIndex)).metadata
        .title
    : null
}

export const getDrumStemSampleCategory = (stem: IStemStore, drumIndex: number) => {
  return ArpRegistry.readyForStudioUse
    ? ArpRegistry.retrieveFromCache((stem as IDrumStemStore).getDrumSample(drumIndex))?.category
    : null
}

export const loadSongFromDraft = (studio: IStudioStore, draft) => {
  studio.loadSongFromDraft(draft)
}

/** Gets the display name for this stem -- either the sample name or "Drums" or "Audio". */
export const getStemDisplayName = (stem: IStemStore) => {
  switch (stem.type()) {
    case StemType.INSTRUMENT:
      return ArpRegistry.retrieveFromCache((stem as IInstrumentStemStore).getMelodicSample())
        ?.category
    case StemType.DRUMS:
      return 'Drums'
    case StemType.AUDIO:
      return 'Audio'
  }
}

/** Gets the display name and details for the stem at the given index. */
export const getStemDisplayData = (
  studio: IStudioStore,
  stemIndex: number,
  isSelectedTrackContainer: boolean = false // we need different information if this is for the STC
) => {
  if (studio.loading || !ArpRegistry.readyForStudioUse) return { category: null, details: null }

  const stem = studio.song.stems[stemIndex]
  const remixedStemId: number = stem.remixedStemId
  let category = ''
  let details = ''
  switch (stem.type()) {
    case StemType.INSTRUMENT:
      const sampleId = (stem as IInstrumentStemStore).getMelodicSample()
      const sample = ArpRegistry.retrieveFromCache(sampleId)
      if (!sample) {
        category = ''
        details = ''
        break
      }
      category = getPrimaryInstrumentFromMedia(sample) || ''
      details = sample.metadata.title
      break
    case StemType.DRUMS:
      category = 'Drums'
      details = `${(stem as IDrumStemStore).drumSamples.length} Samples`
      break
    case StemType.AUDIO:
      const audioStem = stem as IAudioStemStore
      if (isSelectedTrackContainer) {
        if (studio.selectedClipIDs.length > 1) {
          category = 'Audio'
          details = `Multiple Clips`
          break
        }
        const selectedClip = audioStem.clips.find(
          (clip) => clip.uniqueId === studio.selectedClipIDs[0]
        )
        if (selectedClip) {
          const arp = ArpRegistry.retrieveFromCache(selectedClip.sampleWithEffects.sampleIndex)
          if (arp) {
            const title = arp.metadata.title
            const artist = arp.user.displayName
            category = title
            details = artist
            break
          }
        }
      }
      const clipCount = audioStem.clips.length
      category = 'Audio'
      details = `${clipCount} Clip${clipCount !== 1 ? 's' : ''}`

      break
  }

  if (stem.isRemixed) details = `${stem.remixedOgSongName} by ${stem.remixedOgArtist}`
  return {
    category,
    details,
    isRemixed: stem.isRemixed,
    remixedStemId
  }
}

/** should be sent in the DTO that mints the song object */
export const exportStudioForMint = (studio: IStudioStore): SongCompositionDTO => {
  return {
    studioGain: studio.currentGain,
    stems: studio.song.stems.map((stem) => ({
      ...toJS(stem),
      type: stem.type()
    })),
    tempo: studio.song.tempo,
    songLengthInSeconds: studio.song.songMintLengthSeconds
  }
}

export const segmentStudioButton = (button: string, location: string) => {
  window.analytics.track('Studio Button', {
    button: button,
    source: window.location.pathname,
    location: location
  })
}
